import {Subscription} from "rxjs/internal/Subscription"
import {from, interval, Observable, of, Subject, throwError, merge } from "rxjs"
import {catchError, concatAll, filter, throttle, map, mergeMap, take, timeout} from "rxjs/operators"
import {PartialObserver} from "rxjs/internal/types"
import {IEBManagerConnector, IEBMessage} from "./managerConnector"
import {IStatsInputPlugin} from "./statsCollectors"
import {createPeerConnector, gatherIceCandidates, IEBPeerConnector, PeerErrorsType} from "./peerConnector"
import {manager} from "./protos/eb.manager"
import {peers} from "./protos/eb.peers"
import {loginfo, logwarn, logerr, logdebug} from "./utils"
import { networkInterfaces } from "os"
import { O_NOFOLLOW } from "constants"

const modName = "swarm-manager"
const ACTION = manager.ArgumentsEvent.Actions
const EVENTCLASS = manager.ArgumentsEvent.EventClass
interface IEBSwarmManager extends IEBMessage<[string, peers.Message]>, IStatsInputPlugin {
  // All the messages from the peers are emitted
  // Possibly good for receiving chunks from multiple peers
  // This is for storage manager to subscript to
  // Because the storage manager doesn't need to care about which peer sent a chunk normally.
  // But in case the observer need the peer id, the id is emitted as the key for the map.
  next(msg: [string, peers.Message]): Observable<ISwarmErrors>
  broadcastMessage(msg: peers.Message): Observable<ISwarmErrors>
  multicastMessage(msg: peers.Message, destPeers: Observable<[string, IEBPeerConnector]>): Observable<ISwarmErrors>
  select(selector: (pc: IEBPeerConnector) => boolean): Observable<[string, IEBPeerConnector]>
  bind(mgrConnector: IEBManagerConnector): IEBSwarmManager
  setMaxOfferPercentage(set: number): IEBSwarmManager
  register(bandwidth: number, codec: string, url: string): Promise<boolean>
  getPeerChannels(): Observable<[string, peers.Message]>
  getSwarmSize(): IEBSwarmSize
  hasPeer(pid: string): boolean
  start(popInt: number): void
  close(): void
}
interface IEBPeerConnectorUtils {
  createPeerConnector: (id?: string) => IEBPeerConnector
  gatherIceCandidates: () => Observable<manager.IceCandidate[]>
}

interface IEBPeerAdditionalInfo {
  PongRecevied: number
  PingSend: number
  SendSeq: number[] // sequence of counts of msg
  SendTime: number[] // the time accumulated for sending
  InitTimestamp: number
  PeerType: "a" | "b" // a is the initiator, while b is the inverse
  PeerState?: RTCIceConnectionState
  Subscriptions: Subscription[],
  ConnectionTimeout: any
  PingPongRegi: number[]
}

interface IEBSwarmSize {
  All: number
  Offer: number
  Answer: number
}

interface ISwarmErrors {
  Type: SwarmErrorsType,
  Args?: any
}

const defaultPeerConnectorUtils: IEBPeerConnectorUtils = {
  createPeerConnector, gatherIceCandidates,
}
// ISwarmErrors are used by storageManager
enum SwarmErrorsType {
  PeerNoneExisting,
  PeerBroke,
  NoError,
  Timeout,
}


class SwarmManager implements IEBSwarmManager {

  public static new = (): SwarmManager => {
    return new SwarmManager()
  }

  // Add all the configuraBrtion here
  // All the configuration can be done via manager.
  private id: string
  private iceTimeout: number = 8000
  private swarmSize: number = 0
  private maxSwarmSize: number = 10
  private sendMessageTimeout: number = 4000
  // This is the signaling timeout, before 25s, the peer is
  // supposed to finish the signaling process, otherwise is should be deleted
  private connectionTimeout: number = 25000
  private pingPongCheckInterval: number = 10000
  private pingPongInterval: number = 5000
  private pingPongMaxAllowedDrift: number = 10 // once the drift is greater than 10, the peer is deleted
  private offerMaxPercentage: number = 0.5
  private peerList: Map<string, IEBPeerConnector>
  private peerAdditionalInfo: Map<string, IEBPeerAdditionalInfo>
  private managerConnector: IEBManagerConnector
  private peerMessageSubject: Subject<[string, peers.Message]>
  private peerManagerSubscription: Subscription
  private managerStateSubscription: Subscription
  private peerConnectorUtil: IEBPeerConnectorUtils
  private populatingSubscription: Subscription
  private registerParm: manager.ArgumentsRegister

  // the ttl of ice candidate cache
  private iceCandidateCacheTTL: number = 100000
  private lastGatherCalled: number
  private iceCandidateCache: manager.IceCandidate[]

  private eventSubject: Subject<manager.Message>

  public constructor() {
    this.peerMessageSubject = new Subject<[string, peers.Message]>()
    this.peerList = new Map<string, IEBPeerConnector>()
    this.peerAdditionalInfo = new Map<string, IEBPeerAdditionalInfo>()
    this.peerConnectorUtil = defaultPeerConnectorUtils
    this.eventSubject = new Subject<manager.Message>()
    this.eventSubject.subscribe()
  }

  public start = (popInt: number = 4000): void => {
    // start populating
    if (this.populatingSubscription) {
      this.populatingSubscription.unsubscribe()
    }
    this.populatingSubscription = merge(of(0), interval(popInt)).
    pipe(
      filter(() => this.getSwarmSize().All < this.maxSwarmSize),
      throttle((ev) => interval(popInt *
        (Math.pow(this.getSwarmSize().All, 2) + this.maxSwarmSize) / this.maxSwarmSize )),
    ).subscribe({
      next: () => {
        this.managerConnector.next(new manager.Message({
          Type: manager.MessageType.MessageTypeGiveMePeers,
        }))
      },
    })
  }
  public close = (): void => {
    if (this.populatingSubscription) {
      this.populatingSubscription.unsubscribe()
    }
    if (this.peerManagerSubscription) {
      this.peerManagerSubscription.unsubscribe()
    }
    if (this.managerStateSubscription) {
      this.managerStateSubscription.unsubscribe()
    }

    this.peerList.forEach((value) => {
      value.close()
    })
  }

  public ID = (): string => {
    return this.id
  }

  public getPeerChannels = (): Observable<[string, peers.Message]> => {
    return this.peerMessageSubject
  }

  public setPingPongCheckInterval = (set: number): IEBSwarmManager => {
    this.pingPongCheckInterval = set
    return this
  }

  public setMaxSwarmSize = (set: number): IEBSwarmManager => {
    this.maxSwarmSize = set
    return this
  }

  public setPingPongInterval = (set: number): IEBSwarmManager => {
    this.pingPongInterval = set
    return this
  }

  public setMaxOfferPercentage = (set: number): IEBSwarmManager => {
    this.offerMaxPercentage = set
    return this
  }

  public setMaxAllowedPingPongDrift = (set: number): IEBSwarmManager => {
    this.pingPongMaxAllowedDrift = set
    return this
  }

  public setSendMessageTimeout = (set: number): IEBSwarmManager => {
    this.sendMessageTimeout = set
    return this
  }

  // UsePeerConnectorUtil uses a constructor that creates a object implementing IEBPeerConnector
  public usePeerConnectorUtil = (util: IEBPeerConnectorUtils): IEBSwarmManager => {
    this.peerConnectorUtil = util
    return this
  }

  public hasPeer = (pid: string): boolean => {
    return this.peerList.has(pid)
  }

  public select = (selector: (pc: IEBPeerConnector) => boolean):
                            Observable<[string, IEBPeerConnector]> => {
    return from(this.peerList).pipe(
      filter((keyValue) => selector(keyValue[1])),
    )
  }

  public subscribeEvents = (): Subject<manager.Message> => {
    return this.eventSubject
  }

  public getPeerList = (): Map<string, IEBPeerConnector> => {
    return this.peerList
  }

  public getPeerInfo = (): Map<string, IEBPeerAdditionalInfo> => {
    return this.peerAdditionalInfo
  }

  public bind = (managerConnector: IEBManagerConnector): SwarmManager => {
    this.managerConnector = managerConnector
    this.peerManagerSubscription = this.managerConnector.subscribe({
      next: this.handleIncomingMessage,
    })
    this.managerStateSubscription = this.managerConnector.notifyStateChanges({
      next: (state) => {
        if (state === "con" && this.registerParm) {
          this.register(this.registerParm.Bandwidth as number, this.registerParm.Codec, this.registerParm.Url)
        }
      },
    })
    return this
  }

  public register = (bandwidth: number, codec: string, url: string, clean?: boolean): Promise<boolean> => {
    if (clean) {
      this.empty()
    }
    this.registerParm = new manager.ArgumentsRegister({
      Bandwidth: bandwidth,
      Codec: codec,
      Url: url,
    })
    if (
      this.lastGatherCalled &&
      Date.now() - this.lastGatherCalled < this.iceCandidateCacheTTL &&
      this.iceCandidateCache
    ) {
      this.registerParm.Candidates = this.iceCandidateCache
      this.managerConnector.next(new manager.Message({
        Type: manager.MessageType.MessageTypeRegister,
        ArgumentsRegister: this.registerParm,
      }))
      return of(true).toPromise()
    }
    const ob = new Observable<boolean>((subscriber) => {
      this.peerConnectorUtil.gatherIceCandidates().subscribe({
        next: (candidates: manager.IceCandidate[]) => {
          this.iceCandidateCache = candidates
          this.lastGatherCalled = Date.now()
          this.registerParm.Candidates = candidates
          this.managerConnector.next(new manager.Message({
            Type: manager.MessageType.MessageTypeRegister,
            ArgumentsRegister: this.registerParm,
          }))
          subscriber.next(true)
        },
        complete: () => subscriber.complete,
        error: (err: any) => {
          subscriber.next(false)
          logerr(err)
        },
      })
    })
    return ob.toPromise()
  }

  public multicastMessage = (msg: peers.Message, destPeers: Observable<[string, IEBPeerConnector]>):
    Observable<ISwarmErrors> => {
    return destPeers.pipe(
      filter((keyValue) => {
        return this.peerAdditionalInfo.get(keyValue[0]).PeerState === "connected"
      }),
      mergeMap((keyValue) => this.next([keyValue[0], msg])),
    )
  }

  public broadcastMessage = (msg: peers.Message): any => {
    return this.multicastMessage(msg, from(this.peerList))
  }

  // The messages will be notified to the subscriber.
  // One single channel for all peers messages.
  // An abstraction for the storageManager which doesn't care about peer
  public subscribe = (observer?: PartialObserver<[string, peers.Message]>): Subscription => {
    return this.peerMessageSubject.pipe(
      map((value) => {
      return value
    })).subscribe(observer)
  }

  // Next Send the message to peer id given in msg[0]
  public next = (msg: [string, peers.Message]): Observable<ISwarmErrors> => {
    // If the peer connection is not okay, either this method should not be called or this will not result
    const peerConnector = this.peerList.get(msg[0])
    const startTimestamp = Date.now()
    if (!peerConnector) {
      return from([{Type: SwarmErrorsType.PeerNoneExisting}]).pipe(map((value) => throwError(value)), concatAll())
    }
    return peerConnector.next(msg[1]).pipe(
      mergeMap((err) => {
        if (err.Type === PeerErrorsType.NoError) {
          return of({Type: SwarmErrorsType.NoError})
        } else {
          return of({Type: SwarmErrorsType.PeerBroke})
        }
      }),
      timeout(this.sendMessageTimeout),
      catchError(() => of({Type: SwarmErrorsType.Timeout})),
      map((value) => {
        // handling the msg type counts and time
        let idx = 5
        switch (msg[1].Type) {
          case peers.MessageType.MessageTypePossession:
            idx = 2
            break
          case peers.MessageType.MessageTypeRequest:
            idx = 3
            break
          case peers.MessageType.MessageTypeSatisfy:
            idx = 4
            break
          default:
            console.error(modName, "Peer MessageType not handled by stats..", msg[1].Type)
            return value
        }
        this.peerAdditionalInfo.get(msg[0]).SendSeq[idx] += 1
        this.peerAdditionalInfo.get(msg[0]).SendTime[idx] += Date.now() - startTimestamp
        return value
      }),
    )
  }

  public empty = (): void => {
    Array.from(this.peerList.keys()).forEach((pid: string) => {
      // TODO: wait for current task finish.
      this.cleanPeerConnector(this.peerList.get(pid), "reregister")
    })
  }

  public cleanPeerConnector = (pc: IEBPeerConnector, reason: string): any => {
    // TODO: call deconstruct of the peerConnector
    // remove the peer in peerList
    pc.close()
    // TODO: wait for current message to be sent. an interface to check if this peer connector still working.
    if (this.peerAdditionalInfo.has(pc.ID())) {
      loginfo(modName, `cpc: ${pc.ID()} / rs: ${reason}`)
      const pai = this.peerAdditionalInfo.get(pc.ID())
      pai.Subscriptions.forEach((v) => {
        if (v) {
          v.unsubscribe()
        }
      })
        // on the fly ping pong wait. set to dead
      pai.PingPongRegi.forEach((startTime, seqNum) => {
        this.eventSubject.next(new manager.Message({
          Type: 1025,
          ArgumentsEvent: {
            EventName: EVENTCLASS.PINGPONG,
            Action: ACTION.EXCHANGE,
            ArgumentsPingPongExchange: {
              Seq: seqNum,
              Pid: pc.ID(),
              Start: startTime,
              End: Date.now(),
              State: reason,
            },
          },
        }))
      })
    }
    this.clearConnectionTimeoutFor(pc.ID())
    this.peerList.delete(pc.ID())
    this.peerAdditionalInfo.delete(pc.ID())
    pc = null
    this.eventSubject.next(new manager.Message({
      Type: 1025,
      ArgumentsEvent: {
        EventName: EVENTCLASS.SWARM,
        Action: ACTION.SIZEUPDATE,
        ArgumentsSwarmSizeUpdate: {
          Size: this.peerList.size,
        },
      },
    }))
  }

  public getSwarmSize = (): IEBSwarmSize => {
    const result: IEBSwarmSize = {
      All: 0,
      Offer: 0,
      Answer: 0,
    }
    this.peerAdditionalInfo.forEach((value: IEBPeerAdditionalInfo, key: string) => {
      result.All ++
      if (value.PeerType === "a") {result.Offer ++}
      if (value.PeerType === "b") {result.Answer ++}
    })
    return result
  }

  public setupPeer = (pc: IEBPeerConnector, info: IEBPeerAdditionalInfo): IEBPeerConnector => {
    if (!pc.ID()) {
      throw new Error("The peer id is not defined")
    }

    this.peerList.set(pc.ID(), pc)
    this.peerAdditionalInfo.set(pc.ID(), info)

    this.eventSubject.next(new manager.Message({
      Type: 1025,
      ArgumentsEvent: {
        EventName: EVENTCLASS.SWARM,
        Action: ACTION.SIZEUPDATE,
        ArgumentsSwarmSizeUpdate: {
          Size: this.peerList.size,
        },
      },
    }))

    // bind all the peer connector's exchange logics
    let subscriptionPingPong: Subscription
    let subscriptionPingPongCheck: Subscription
    let subscriptionMessage: Subscription
    // checking the signaling timeout
    const connectionBomb = setTimeout(() => {
      clearTimeout(connectionBomb)
      if (pc.getState() === "connected") { return }
      // remove the peer connector
      this.managerConnector.next(new manager.Message({
        Type: manager.MessageType.MessageTypeSwipeLeft,
        ArgumentsSwipeLeft: {
          Peer: pc.ID(),
        },
      }))
      this.cleanPeerConnector(pc, "Connection")
    }, this.connectionTimeout)

    const subscriptionStateChange = pc.notifyStateChanges({
      next: (state: RTCIceConnectionState) => {
        // Handle peer stats
        if (this.peerAdditionalInfo.has(pc.ID())) {
          this.peerAdditionalInfo.get(pc.ID()).PeerState = state
        } else {
          console.warn(modName, "peer not in peerlist, yet still listening to stateChanges", pc.ID())
          return
        }
        switch (state) {
          case "new":
          // case "connecting":
            break
          case "connected":
            // Collector: send RTC peer Connection connected msg
            loginfo(modName, "peer connected: " + pc.ID())
            // remove the timeout
            this.clearConnectionTimeoutFor(pc.ID())
            this.eventSubject.next(new manager.Message({
              Type: 1025,
              ArgumentsEvent: {
                EventName: EVENTCLASS.RTC,
                Action: ACTION.OPEN,
                PeerID: this.id,
                RemotePeerID: pc.ID(),
              },
            }))
            // whenever connected, all the message loop starts
            // subscribe to pc's message channel
            if (subscriptionMessage) { subscriptionMessage.unsubscribe() }
            subscriptionMessage = pc.subscribe({
              next: (pm: peers.Message) => {
                if (!this.peerAdditionalInfo.has(pc.ID())) {
                  subscriptionMessage ? subscriptionMessage.unsubscribe() : (() => {})()   // pass
                  return
                }
                if (pm.Type === peers.MessageType.MessageTypePing) {
                  pc.next(new peers.Message({
                    Type: peers.MessageType.MessageTypePong,
                    ArgumentsPingPong: {
                      Seq: pm.ArgumentsPingPong.Seq,
                    },
                  })).subscribe({
                    next: () => {
                      this.peerAdditionalInfo.get(pc.ID()).SendSeq[1] += 1
                    },
                    error: (ev) => {
                      console.warn(modName, `Failed to send pong sequence ${pc.ID()}: ${ev}`)
                    },
                  })
                }
                if (pm.Type === peers.MessageType.MessageTypePong) {
                  const seq = pm.ArgumentsPingPong.Seq // tpi = ThisPeerInfo
                  const tpi = this.peerAdditionalInfo.get(pc.ID())
                  tpi.PongRecevied = seq
                  if (tpi.PingPongRegi[seq]) {
                    // only send a ping pong exchange when we have sent the ping
                    this.eventSubject.next(new manager.Message({
                      Type: manager.MessageType.MessageTypeEvent,
                      ArgumentsEvent: {
                        EventName: EVENTCLASS.PINGPONG,
                        Action: ACTION.EXCHANGE,
                        ArgumentsPingPongExchange: {
                          Pid: pc.ID(),
                          Seq: seq,
                          Start: tpi.PingPongRegi[seq],
                          End: Date.now(),
                          State: "normal",
                        },
                      },
                    }))
                    // remove the on the fly waiting ping
                    delete tpi.PingPongRegi[seq]
                  }
                }
                this.peerMessageSubject.next([pc.ID(), pm])
              },
              error: (ev: any) => {}, // TODO: error handling for receiving messages??? not neccessary
              complete: () => {},
            })

            if (subscriptionPingPong) { subscriptionPingPong.unsubscribe() }
            subscriptionPingPong = interval(this.pingPongInterval).subscribe((seq) => {
              if (!this.peerAdditionalInfo.has(pc.ID())) {
                subscriptionPingPong ? subscriptionPingPong.unsubscribe() : (() => {})()
                return
              }
              this.peerAdditionalInfo.get(pc.ID()).PingSend = seq
              pc.next(new peers.Message({
                Type: peers.MessageType.MessageTypePing,
                ArgumentsPingPong: {
                  Seq: seq,
                },
              })).subscribe({
                next: () => {
                  this.peerAdditionalInfo.get(pc.ID()).SendSeq[0] += 1
                  this.peerAdditionalInfo.get(pc.ID()).PingPongRegi[seq] = Date.now()
                },
                error: (ev) => {console.warn(modName, `Failed to send ping sequence ${pc.ID()}: ${ev}`)},
              })
            })

            if (subscriptionPingPongCheck) { subscriptionPingPongCheck.unsubscribe() }
            subscriptionPingPongCheck = interval(this.pingPongCheckInterval).subscribe(() => {
              if (!this.peerAdditionalInfo.has(pc.ID())) {
                subscriptionPingPongCheck ? subscriptionPingPongCheck.unsubscribe() : (() => {})()
                return
              }
              const addInfo = this.peerAdditionalInfo.get(pc.ID())
              if (addInfo.PingSend - addInfo.PongRecevied < this.pingPongMaxAllowedDrift) { return }
              this.cleanPeerConnector(pc, "pingpong")
            })
            this.peerAdditionalInfo.get(pc.ID()).Subscriptions = [
              subscriptionPingPong,
              subscriptionMessage,
              subscriptionPingPongCheck,
              subscriptionStateChange,
            ]
            this.peerAdditionalInfo.get(pc.ID()).ConnectionTimeout = connectionBomb
            break
          case "disconnected":
          case "failed":
            this.cleanPeerConnector(pc, "disconnected")
            // Collector: send RTC peer Connection connected msg
            this.eventSubject.next(new manager.Message({
              Type: 1025,
              ArgumentsEvent: {
                EventName: EVENTCLASS.RTC,
                Action: ACTION.CLOSE,
                PeerID: this.id,
                RemotePeerID: pc.ID(),
              },
            }))
            break
        }
      },
      complete: () => {
      },
    })
    this.peerAdditionalInfo.get(pc.ID()).Subscriptions = [
      subscriptionPingPong,
      subscriptionMessage,
      subscriptionPingPongCheck,
      subscriptionStateChange,
    ]
    return pc
  }
  private clearConnectionTimeoutFor = (pid: string) => {
    if (this.peerAdditionalInfo.has(pid) && this.peerAdditionalInfo.get(pid).ConnectionTimeout) {
      clearTimeout(this.peerAdditionalInfo.get(pid).ConnectionTimeout)
      this.peerAdditionalInfo.get(pid).ConnectionTimeout = null
    }
  }

  private handleIncomingMessage = (msg: manager.Message) => {
    // TODO: add peer to the peerlist
    // TODO: add peerinfo to the peerAdditionalInfo
    switch (msg.Type) {
      case manager.MessageType.MessageTypeRegistered:
        if (!msg.ArgumentsRegistered || !msg.ArgumentsRegistered.ID) {
          console.error(modName, "Critical error: msg Registered not correct")
          break
        }
        this.id = msg.ArgumentsRegistered.ID
        this.managerConnector.setID(this.id)
        this.eventSubject.next(new manager.Message({
          Type: 1025,
          ArgumentsEvent: {
            EventName: EVENTCLASS.PEER,
            Action: ACTION.REGISTERED,
            ArgumentsRegistered: msg.ArgumentsRegistered,
          },
        }))
        // when registered ask for peers.
        this.managerConnector.next(new manager.Message({
          Type: manager.MessageType.MessageTypeGiveMePeers,
        }))
        break
      case manager.MessageType.MessageTypeHereAreSomePeers:
        const swarmSize = this.getSwarmSize()
        from(msg.ArgumentsHereAreSomePeers.Peers).
          pipe(
            // filter out the peerConnections in peerList
            filter((value) => !this.peerList.has(value)),
            // take only certain number of peer id from the manager
            take(this.maxSwarmSize * this.offerMaxPercentage - swarmSize.Offer),
          ).subscribe({
            next: (peerID: string) => {
              const pc = this.setupPeer(this.peerConnectorUtil.createPeerConnector(peerID), {
                PeerType: "a",
                PingSend: 0,
                InitTimestamp: Date.now(),
                SendSeq: [0, 0, 0, 0, 0],
                SendTime: [0, 0, 0, 0, 0],
                PongRecevied: 0,
                Subscriptions: null,
                ConnectionTimeout: null,
                PingPongRegi: [],
              })
              // Collector: send RTC peer Connection init msg
              this.eventSubject.next(new manager.Message({
                Type: 1025,
                ArgumentsEvent: {
                  EventName: EVENTCLASS.RTC,
                  Action: ACTION.INIT,
                  PeerID: this.id,
                  RemotePeerID: peerID,
                },
              }))
              pc.sendOffer().subscribe({
                next: (offer: [string, string[]]) => {
                  if (offer[1].length === 0 ) {
                    console.warn(modName, "nothing to offer, offer[1].length===0")
                  } // what to do if there is no ice candidates? d! or w
                  const offerMsg = new manager.Message({
                    Type: manager.MessageType.MessageTypeSwipeRight,
                    ArgumentsSwipeRight: {
                      Peer: peerID,
                      Offer: offer[0],
                      Candidates: offer[1],
                    },
                  })
                  this.managerConnector.next(offerMsg)
                },
                error: (ev) => {
                },
                complete: () => {
                },
              })
            },
            complete: () => {},
          })
        break
      case manager.MessageType.MessageTypePairingRequest:
        if (msg.ArgumentsPairingRequest.Type === manager.PairingRequestType.PairingRequestType_answer) {
          if (this.peerList.has(msg.ArgumentsPairingRequest.Peer)) {
            const desc = msg.ArgumentsPairingRequest.Offer
            const candi = msg.ArgumentsPairingRequest.Candidates
            const pc = this.peerList.get(msg.ArgumentsPairingRequest.Peer)
            pc.receiveAnswer(desc, candi).subscribe({
                next: () => {},
                complete: () => {},
                error: (err) => {
                },
              })
          }
        } else if (msg.ArgumentsPairingRequest.Type === manager.PairingRequestType.PairingRequestType_offer) {
          const currentSwarmSize = this.getSwarmSize()
          const peerID = msg.ArgumentsPairingRequest.Peer
          if (this.peerList.has(peerID)) { break }
          if (currentSwarmSize.Answer >= this.maxSwarmSize * ( 1 - this.offerMaxPercentage)) { break }
          const pc = this.setupPeer(this.peerConnectorUtil.createPeerConnector(peerID), {
            PeerType: "b",
            PingSend: 0,
            PongRecevied: 0,
            InitTimestamp: Date.now(),
            SendSeq: [0, 0, 0, 0, 0],
            SendTime: [0, 0, 0, 0, 0],
            Subscriptions: null,
            ConnectionTimeout: null,
            PingPongRegi: [],
          })
          pc.sendAnswer(
            msg.ArgumentsPairingRequest.Offer,
            msg.ArgumentsPairingRequest.Candidates,
          ).subscribe({
            next: (answer: [string, string[]]) => {
              const offerMsg = new manager.Message({
                Type: manager.MessageType.MessageTypeItsAMatch,
                ArgumentsItsAMatch: {
                  Peer: peerID,
                  Offer: answer[0],
                  Candidates: answer[1],
                },
              })
              this.managerConnector.next(offerMsg)
            },
            error: (ev) => {
            },
            complete: () => {},
          })
        }
        break
    }
  }
}

export { SwarmManager, IEBSwarmManager, IEBPeerConnectorUtils, ISwarmErrors, SwarmErrorsType }
